<script lang="ts">
import {
  defineComponent, onBeforeUnmount, PropType, watch, toRef,
} from 'vue';
import { ImageEnhancementOutputs } from 'vue-media-annotator/use/useImageEnhancements';
import { StitchedSide, stitchedFrame } from 'vue-media-annotator/stitchedStereo';
import { Flick, SetTimeFunc } from '../../use/useTimeObserver';
import AnnotatorImageCursor from './AnnotatorImageCursor.vue';
import useAnnotatorImageCursor from './useAnnotatorImageCursor';
import { injectCameraInitializer } from './useMediaController';
import { kwiverSeek, OnePTSTick } from './videoSeek';

export default defineComponent({
  name: 'VideoAnnotator',
  components: { AnnotatorImageCursor },
  props: {
    videoUrl: {
      type: String,
      required: true,
    },
    videoPlayerAttributes: {
      type: Object as PropType<{ [key: string]: string }>,
      default: () => ({}),
    },
    frameRate: {
      type: Number,
      required: true,
    },
    updateTime: {
      type: Function as PropType<SetTimeFunc>,
      required: true,
    },
    originalFps: {
      type: Number as PropType<number | null>,
      default: null,
    },
    camera: {
      type: String as PropType<string>,
      default: 'singleCam',
    },
    imageEnhancementOutputs: {
      type: Object as PropType<ImageEnhancementOutputs>,
      default: () => ({
        brightness: { slope: 1, intercept: 0 },
        contrast: { slope: 1, intercept: 0.5 },
        saturation: { values: 1 },
        sharpen: { kernelMatrix: '0 -1 0 -1 5 -1 0 -1 0', divisor: 1 },
      }),
    },
    isDefaultImage: {
      type: Boolean,
      default: true,
    },
    filterId: {
      type: String as PropType<string>,
      default: 'imageEnhancements',
    },
    /** Show only this half of each frame (stitched stereo). */
    stitchedSide: {
      type: String as PropType<StitchedSide | null>,
      default: null,
    },
  },
  setup(props, { emit }) {
    const cameraInitializer = injectCameraInitializer();
    const {
      state: data,
      geoViewer,
      cursorHandler,
      imageCursor,
      container,
      initializeViewer,
      mediaController,
      externallyDriven,
    } = cameraInitializer(props.camera, 'video', {
      // allow hoisting for these functions.
      seek,
      pause,
      play,
      setVolume,
      setSpeed,
    });
    const { playbackCursor } = useAnnotatorImageCursor(
      toRef(data, 'imageCursor'),
      toRef(data, 'cursor'),
      toRef(data, 'imageCursorEditing'),
    );
    function makeVideo() {
      const video = document.createElement('video');
      video.preload = 'auto';
      video.src = props.videoUrl;
      Object.assign(video, props.videoPlayerAttributes);
      return video;
    }
    const video = makeVideo();
    onBeforeUnmount(() => {
      if (video) {
        video.pause();
      }
    });
    async function seek(frame: number | undefined) {
      if (frame === undefined) {
        // No frame for this camera at the current aligned-timeline slot: blank
        // the pane. Leaves data.frame untouched -- it's read elsewhere (e.g.
        // annotation-overlay lookups) and this phase doesn't touch annotation
        // storage. In practice unreachable today since a video-backed camera
        // (empty imageData) always disqualifies the whole dataset from aligned
        // mode (see alignedTimeline.ts's canAlign) -- kept for symmetry/safety.
        data.hasFrame = false;
        if (quadFeatureLayer !== undefined) {
          quadFeatureLayer.node().css('visibility', 'hidden');
        }
        return;
      }
      if (!data.hasFrame) {
        data.hasFrame = true;
        if (quadFeatureLayer !== undefined) {
          quadFeatureLayer.node().css('visibility', '');
        }
      }
      /** Only perform seek for whole frame numbers */
      const requestedFrame = Math.round(frame);
      /** Different seek approaches based on known information */
      if (props.originalFps) {
        /** If the video's true FPS is known */
        data.currentTime = kwiverSeek(frame, props.frameRate, props.originalFps);
      } else {
        /** Else fall back to a reasonable default */
        data.currentTime = frame / props.frameRate + OnePTSTick;
      }
      video.currentTime = data.currentTime;
      data.frame = requestedFrame;
      data.flick = Math.round(data.currentTime * Flick);
      props.updateTime(data);
    }
    function pause() {
      video.pause();
      seek(data.frame); // snap to frame boundary
      data.playing = false;
      props.updateTime(data);
    }
    function syncWithVideo() {
      if (data.playing) {
        const newFrame = video.currentTime * props.frameRate;
        if (newFrame > data.maxFrame) {
          /** Video has played past its allowed truncated end, seek to end */
          data.frame = data.maxFrame;
          pause();
          return;
        }
        data.frame = Math.floor(newFrame);
        data.flick = Math.round(video.currentTime * Flick);
        data.syncedFrame = data.frame;
        // Keep shared time.frame in sync so Timeline playhead tracks playback
        props.updateTime(data);
        geoViewer.value.scheduleAnimationFrame(syncWithVideo);
      }
      data.currentTime = video.currentTime;
    }
    async function play() {
      try {
        await video.play();
        data.playing = true;
        props.updateTime(data);
        // When a global aligned timeline is driving playback, the aggregate
        // controller's own centralized tick calls seek() directly -- this
        // camera must not also free-run its own loop.
        if (!externallyDriven.value) {
          syncWithVideo();
        }
      } catch (ex) {
        console.error(ex);
      }
    }
    function logError(event: ErrorEvent) {
      console.error('Media failed to initialize', event);
      emit('load-error', 'Could not load the video. The file may have been moved or deleted.');
    }
    function setVolume(level: number) {
      video.volume = level;
      data.volume = video.volume;
    }
    function setSpeed(level: number) {
      video.playbackRate = level;
      data.speed = video.playbackRate;
    }
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    let quadFeatureLayer = undefined as any;
    watch(
      () => props.isDefaultImage,
      (newVal) => {
        if (quadFeatureLayer !== undefined) {
          if (newVal) {
            quadFeatureLayer.node().css('filter', '');
          } else {
            quadFeatureLayer.node().css('filter', `url(#${props.filterId})`);
          }
          data.imageRevision += 1;
        }
      },
      { deep: true },
    );
    /**
     * Initialize the Quad feature layer once
     * video metadata has been fetched.
     */
    function loadedMetadata() {
      video.removeEventListener('loadedmetadata', loadedMetadata);
      const { width, height, crop } = stitchedFrame(
        props.stitchedSide,
        video.videoWidth,
        video.videoHeight,
      );
      const maybeMaxFrame = Math.floor(props.frameRate * video.duration);
      if (props.originalFps !== null) {
        /**
         * Don't allow the user to seek past the final frame as defined by kwiver.
         */
        if (kwiverSeek(maybeMaxFrame, props.frameRate, props.originalFps) > video.duration) {
          data.maxFrame = maybeMaxFrame - 1;
        } else {
          data.maxFrame = maybeMaxFrame;
        }
      } else {
        console.warn('Dataset loaded without originalFps, seeking accuracy will be impacted');
        data.maxFrame = maybeMaxFrame;
      }
      initializeViewer(width, height);
      quadFeatureLayer = geoViewer.value.createLayer('feature', {
        features: ['quad.video'],
        autoshareRenderer: false,
      });
      quadFeatureLayer
        .createFeature('quad')
        .data([
          {
            ul: { x: 0, y: 0 },
            lr: { x: width, y: height },
            video,
            ...(crop ? { crop } : {}),
          },
        ])
        .draw();
      // The <video> element renders in place from here on, so this is the
      // one swap imageRevision watchers ever see for a video pane.
      data.imageRevision += 1;
      // Force the first frame to load on slow networks.
      // See https://github.com/Kitware/dive/issues/447 for more details.
      seek(0);
      if (!props.isDefaultImage) {
        quadFeatureLayer.node().css('filter', `url(#${props.filterId})`);
      }
      data.ready = true;
      data.volume = video.volume;
      data.speed = video.playbackRate;
      data.currentTime = video.currentTime;
      data.duration = video.duration;
    }
    // Watch brightness for change, only set filter if value
    // is switching from number -> undefined, or vice versa.
    function pendingUpdate() {
      data.syncedFrame = Math.round(video.currentTime * props.frameRate);
      // The aligned-view warp is a canvas snapshot of this <video> element,
      // redrawn only on an imageRevision bump -- unlike the native pane,
      // which the browser keeps live on its own. loadedmetadata bumps it
      // once for the initial frame; without another bump here, a scrub
      // leaves the warp showing whatever the video displayed mid-seek
      // (often a black frame) instead of the frame the seek landed on.
      data.imageRevision += 1;
    }
    video.addEventListener('loadedmetadata', loadedMetadata);
    video.addEventListener('seeked', pendingUpdate);
    video.addEventListener('error', logError);
    return {
      data,
      playbackCursor,
      imageCursorRef: imageCursor,
      containerRef: container,
      cursorHandler,
      mediaController,
    };
  },
});
</script>

<template>
  <div class="video-annotator" :style="{ cursor: data.cursor }">
    <svg width="0" height="0" style="position: absolute; top: -1px; left: -1px">
      <defs>
        <filter :id="filterId">
          <feComponentTransfer id="feBrightness">
            <feFuncR
              type="linear"
              :slope="imageEnhancementOutputs.brightness.slope"
              :intercept="imageEnhancementOutputs.brightness.intercept"
            />
            <feFuncG
              type="linear"
              :slope="imageEnhancementOutputs.brightness.slope"
              :intercept="imageEnhancementOutputs.brightness.intercept"
            />
            <feFuncB
              type="linear"
              :slope="imageEnhancementOutputs.brightness.slope"
              :intercept="imageEnhancementOutputs.brightness.intercept"
            />
          </feComponentTransfer>
          <!-- Contrast -->
          <feComponentTransfer id="feContrast">
            <feFuncR
              type="linear"
              :slope="imageEnhancementOutputs.contrast.slope"
              :intercept="imageEnhancementOutputs.contrast.intercept"
            />
            <feFuncG
              type="linear"
              :slope="imageEnhancementOutputs.contrast.slope"
              :intercept="imageEnhancementOutputs.contrast.intercept"
            />
            <feFuncB
              type="linear"
              :slope="imageEnhancementOutputs.contrast.slope"
              :intercept="imageEnhancementOutputs.contrast.intercept"
            />
          </feComponentTransfer>
          <!-- Saturation -->
          <feColorMatrix
            id="feSaturate"
            type="saturate"
            :values="imageEnhancementOutputs.saturation.values.toString()"
          />
          <!-- Sharpening -->
          <feConvolveMatrix
            id="feSharpen"
            order="3"
            :divisor="imageEnhancementOutputs.sharpen.divisor"
            :kernelMatrix="imageEnhancementOutputs.sharpen.kernelMatrix"
            edgeMode="duplicate"
          />
          <feComposite in2="SourceGraphic" operator="in" />
        </filter>
      </defs>
    </svg>
    <div
      ref="imageCursorRef"
      class="imageCursor"
    >
      <AnnotatorImageCursor
        :image-cursor="data.imageCursor"
        :image-cursor-editing="data.imageCursorEditing"
        :cursor="data.cursor"
      />
    </div>
    <div
      ref="containerRef"
      class="playback-container"
      :style="{ cursor: playbackCursor }"
      @mousemove="cursorHandler.handleMouseMove"
      @mouseleave="cursorHandler.handleMouseLeave"
      @mouseover="cursorHandler.handleMouseEnter"
    />
    <div
      v-if="data.ready && !data.hasFrame"
      class="no-frame-overlay"
    >
      No frame at this instant
    </div>
    <slot name="control" />
    <slot v-if="data.ready" />
  </div>
</template>

<style lang="scss" scoped>
@import "./annotator.scss";
</style>
